import type { LivePeer } from "./livePeer";

export interface VoiceSide {
  level: number;
  bands: Float32Array;
}

export interface VoiceLevelsReading {
  user: VoiceSide;
  zoya: VoiceSide;
}

export const VOICE_BAND_COUNT = 64;

const FFT_SIZE = 1024;
const LOW_HZ = 120;
const HIGH_HZ = 4600;

interface Tap {
  stream: MediaStream;
  source: MediaStreamAudioSourceNode;
  analyser: AnalyserNode;
}

let context: AudioContext | null = null;
let peer: LivePeer | null = null;
let micTap: Tap | null = null;
let zoyaTap: Tap | null = null;
const samples = new Float32Array(FFT_SIZE);
const spectrum = new Uint8Array(FFT_SIZE / 2);
const reading: VoiceLevelsReading = {
  user: { level: 0, bands: new Float32Array(VOICE_BAND_COUNT) },
  zoya: { level: 0, bands: new Float32Array(VOICE_BAND_COUNT) },
};

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));

const openTap = (stream: MediaStream): Tap | null => {
  if (!context || stream.getAudioTracks().length === 0) return null;
  const analyser = context.createAnalyser();
  analyser.fftSize = FFT_SIZE;
  analyser.smoothingTimeConstant = 0.55;
  analyser.minDecibels = -90;
  analyser.maxDecibels = -14;
  const source = context.createMediaStreamSource(stream);
  source.connect(analyser);
  return { stream, source, analyser };
};

const closeTap = (tap: Tap | null) => {
  if (!tap) return;
  try {
    tap.source.disconnect();
  } catch {}
};

const closeTaps = () => {
  closeTap(micTap);
  closeTap(zoyaTap);
  micTap = null;
  zoyaTap = null;
};

const measure = (tap: Tap | null, side: VoiceSide) => {
  if (!tap || !context) {
    side.level = 0;
    side.bands.fill(0);
    return;
  }
  tap.analyser.getFloatTimeDomainData(samples);
  let sum = 0;
  for (let i = 0; i < samples.length; i += 1) sum += samples[i] * samples[i];
  const db = 20 * Math.log10(Math.sqrt(sum / samples.length) + 1e-9);
  side.level = Math.pow(clamp01((db + 54) / 44), 1.4);
  tap.analyser.getByteFrequencyData(spectrum);
  const nyquist = context.sampleRate / 2;
  for (let band = 0; band < VOICE_BAND_COUNT; band += 1) {
    const low = LOW_HZ * Math.pow(HIGH_HZ / LOW_HZ, band / VOICE_BAND_COUNT);
    const high = LOW_HZ * Math.pow(HIGH_HZ / LOW_HZ, (band + 1) / VOICE_BAND_COUNT);
    const from = Math.floor((low / nyquist) * spectrum.length);
    const to = Math.max(from + 1, Math.ceil((high / nyquist) * spectrum.length));
    let peak = 0;
    for (let bin = from; bin < to; bin += 1) peak = Math.max(peak, spectrum[bin]);
    side.bands[band] = Math.pow(clamp01((peak / 255 - 0.35) / 0.65), 1.3);
  }
};

export const voiceLevels = {
  prepare: () => {
    const Context = window.AudioContext || (window as any).webkitAudioContext;
    if (!Context) return;
    if (!context) context = new Context();
    if (context.state === "suspended") void context.resume().catch(() => undefined);
  },
  attach: (next: LivePeer) => {
    closeTaps();
    peer = next;
  },
  detach: () => {
    closeTaps();
    peer = null;
    if (context?.state === "running") void context.suspend().catch(() => undefined);
  },
  read: (): VoiceLevelsReading => {
    if (peer && context) {
      if (micTap?.stream !== peer.mic) {
        closeTap(micTap);
        micTap = openTap(peer.mic);
      }
      const remote = peer.audio.srcObject;
      if (remote instanceof MediaStream && zoyaTap?.stream !== remote) {
        closeTap(zoyaTap);
        zoyaTap = openTap(remote);
      }
    }
    measure(micTap, reading.user);
    measure(zoyaTap, reading.zoya);
    return reading;
  },
};
